import { useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";

import { ReactComponent as CheckIcon } from "assets/images/iconsV2/check.svg";
import { ReactComponent as CopyIcon } from "assets/images/iconsV2/copy.svg";
import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import Button from "components/v2/Button/Button";
import ChoiceChip from "components/v2/ChoiceChip/ChoiceChip";
import { StopTimeType, StopType } from "types/MultiStop";
import FormSectionLabel from "pages/shipper/RequestFormV2/components/FormSectionLabel/FormSectionLabel";
import { usePersistDraft } from "@RequestFormV2/hooks/usePersistDraft";
import { getStopFieldPath } from "@RequestFormV2/sections/Schedule/utils/fieldPaths";
import { AT_LEAST_ONE_DELIVERY_ERROR_PATH } from "@RequestFormV2/sections/Schedule/validation/validateSchedule";

interface StopHeaderProps {
  index: number;
  canRemove: boolean;
  onDuplicate: (index: number) => void;
  onRemove: (index: number) => void;
}

export default function StopHeader({ index, canRemove, onDuplicate, onRemove }: StopHeaderProps) {
  const intl = useIntl();
  const { watch, setValue, getValues, clearErrors } = useFormContext();
  const persistDraft = usePersistDraft();

  const stopType = watch(getStopFieldPath(index, "stop_type"));

  const handleChangeType = (value: StopType) => {
    clearErrors(AT_LEAST_ONE_DELIVERY_ERROR_PATH);
    setValue(getStopFieldPath(index, "stop_type"), value);
    setValue(`schedule.stops.${index}.stop_key`, crypto.randomUUID());

    // Keep the planning radios logically consistent with the new stop type.
    const currentTimeType = getValues(getStopFieldPath(index, "stop_time_type"));
    if (currentTimeType === StopTimeType.BEFORE && value === StopType.PICKUP) {
      setValue(getStopFieldPath(index, "stop_time_type"), StopTimeType.AFTER);
    }
    if (currentTimeType === StopTimeType.AFTER && value === StopType.DROPOFF) {
      setValue(getStopFieldPath(index, "stop_time_type"), StopTimeType.BEFORE);
    }

    persistDraft("schedule");
  };

  const stopTypeOptions = [
    {
      label: intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" }),
      value: StopType.PICKUP,
      ...(stopType === StopType.PICKUP && { icon: <CheckIcon /> }),
    },
    {
      label: intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" }),
      value: StopType.DROPOFF,
      ...(stopType === StopType.DROPOFF && { icon: <CheckIcon /> }),
      // The first stop is always the pickup; only later stops can become a delivery.
      disabled: index === 0,
    },
  ];

  return (
    <div className="rfv2-form-section rfv2-schedule-stop__header">
      <FormSectionLabel
        label={intl.formatMessage(
          { id: "request-form.schedule.stopNumber", defaultMessage: "Stop #{number}" },
          { number: index + 1 },
        )}
      />
      <div className="rfv2-form-section__content rfv2-schedule-stop__header-content">
        <ChoiceChip
          options={stopTypeOptions}
          selected={stopType}
          onChange={(value) => handleChangeType(value as StopType)}
        />
        <div className="rfv2-schedule-stop__header-actions">
          <Button
            variant="tertiary-gray"
            iconLeft={<CopyIcon />}
            aria-label={intl.formatMessage({ id: "general.duplicate", defaultMessage: "Duplicate" })}
            onClick={() => onDuplicate(index)}
          />
          {canRemove && index > 0 && (
            <Button
              variant="link-error"
              iconLeft={<TrashIcon />}
              aria-label={intl.formatMessage({ id: "general.delete", defaultMessage: "Delete" })}
              onClick={() => onRemove(index)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
